import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { isQueueableRequest, isNetworkError, enqueue, flushQueue, queueLength } from './offlineQueue';
import { isCacheableGet, hasCached, getCached, setCached } from './offlineCache';
import storage from './nativeStorage';

// Relative '/api' only resolves correctly in the browser/PWA, where Nginx
// reverse-proxies /api to the backend on the same origin. A Capacitor native
// build's WebView origin has no such proxy, so it must call the real API
// host directly. Defaults to staging so a forgotten env flag can never
// accidentally ship a production-pointed native build.
const NATIVE_API_BASE = process.env.REACT_APP_API_ENV === 'production'
  ? 'https://golfsocietymanager.com/api'
  : 'https://staging.golfsocietymanager.com/api';

const baseURL = Capacitor.isNativePlatform() ? NATIVE_API_BASE : '/api';

const api = axios.create({ baseURL });

api.interceptors.request.use(config => {
  const token = storage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => {
    // Write-through cache: every successful GET on a cacheable setup-data
    // path refreshes the offline copy, so the most recent online view is
    // always what a later offline GET falls back to.
    if ((res.config.method || '').toLowerCase() === 'get' && isCacheableGet(res.config.url)) {
      setCached(res.config.url, res.data);
    }
    return res;
  },
  err => {
    if (err.response?.status === 401) {
      storage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(err);
    }

    const method = (err.config?.method || '').toLowerCase();
    const url = err.config?.url;

    // GET network failure on a cacheable setup-data path: fall back to the
    // last-known-good cached response instead of failing the screen -- but
    // only if we actually have something cached (never fabricate empty data,
    // which would look like "no players/no groups" instead of "no signal").
    if (method === 'get' && isNetworkError(err) && isCacheableGet(url) && hasCached(url)) {
      return Promise.resolve({
        data: getCached(url), status: 0, statusText: 'Served from offline cache',
        headers: {}, config: err.config, cached: true,
      });
    }

    // Setup-screen write (and score save/clear) while offline: queue it
    // locally and resolve as "queued" instead of failing, so the calling
    // page doesn't need special-case error handling. Widened from
    // POST-only/scores-only to a curated PUT+POST allow-list -- see
    // offlineQueue.js. DELETE is never included.
    if (isNetworkError(err) && isQueueableRequest(method, url)) {
      let body = {};
      try { body = JSON.parse(err.config.data || '{}'); } catch { /* leave as {} */ }
      enqueue(err.config.url, err.config.method, body);
      return Promise.resolve({ data: { queued: true }, status: 0, statusText: 'Queued offline', headers: {}, config: err.config });
    }

    return Promise.reject(err);
  }
);

const tryFlush = () => { if (queueLength() > 0) flushQueue(api); };
window.addEventListener('online', tryFlush);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') tryFlush();
});
setInterval(tryFlush, 45000);

export default api;
